import React, { useEffect, useState } from "react";
import {
  Wallet, ShieldCheck, Copy, RefreshCw, Check, X, Settings2, Landmark, ReceiptText,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input, Select, Textarea } from "@/components/Field";

const PAYMENT_TYPES = ["one_time", "deposit", "partial", "milestone"];
const TYPE_LABEL = {
  de: { one_time: "Einmalzahlung", deposit: "Anzahlung", partial: "Teilzahlung", milestone: "Meilenstein" },
  en: { one_time: "One-time", deposit: "Deposit", partial: "Partial", milestone: "Milestone" },
};

const statusColor = (s) => {
  if (["paid", "authorized"].includes(s)) return "bg-green-500/15 text-green-500";
  if (["failed", "cancelled", "returned", "disputed"].includes(s)) return "bg-red-500/15 text-red-500";
  if (["refunded", "partially_refunded"].includes(s)) return "bg-blue-500/15 text-blue-500";
  return "bg-amber-500/15 text-amber-500";
};

export default function PaymentConfig() {
  const { lang, sl } = useI18n();
  const de = lang === "de";
  const [tab, setTab] = useState("methods");
  const [methods, setMethods] = useState([]);
  const [providers, setProviders] = useState([]);
  const [webhook, setWebhook] = useState({});
  const [edit, setEdit] = useState(null);        // method being edited
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ status: "", provider: "", mode: "", q: "" });
  const [invoices, setInvoices] = useState([]);
  const [manual, setManual] = useState({ invoice_id: "", amount: "", note: "" });

  const loadMethods = () => api.get("/admin/payments/methods").then((r) => setMethods(r.data)).catch((e) => toast.error(apiErr(e)));
  const loadProviders = () => api.get("/admin/payments/providers").then((r) => setProviders(r.data)).catch(() => {});
  const loadWebhook = () => api.get("/admin/payments/webhook-url").then((r) => setWebhook(r.data)).catch(() => {});
  const loadLogs = () => api.get("/admin/payments/logs", { params: logFilter }).then((r) => setLogs(r.data)).catch(() => {});
  const loadInvoices = () => api.get("/invoices").then((r) => setInvoices((r.data || []).filter((i) => i.status !== "paid"))).catch(() => {});

  useEffect(() => { loadMethods(); loadProviders(); loadWebhook(); }, []);
  useEffect(() => { if (tab === "logs") loadLogs(); if (tab === "bank") loadInvoices(); }, [tab, logFilter]); // eslint-disable-line

  const bankMethod = methods.find((m) => m.key === "bank_transfer");

  const toggleMethod = async (m, enabled) => {
    try {
      const payload = { enabled };
      if (m.provider_mode === "live") {
        const cp = window.prompt(de ? "LIVE-Modus: Admin-Passwort bestätigen" : "LIVE mode: confirm admin password");
        if (!cp) return;
        payload.confirm_password = cp;
      }
      await api.put(`/admin/payments/methods/${m.key}`, payload);
      toast.success(de ? "Aktualisiert" : "Updated");
      loadMethods();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(de ? "Kopiert" : "Copied"); };

  const Tab = ({ id, label, Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`paytab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md ${tab === id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-black flex items-center gap-2">
          <Wallet size={22} className="text-primary" /> {de ? "Zahlungsarten & Zahlungsanbieter" : "Payment Methods & Providers"}
        </h1>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-500/15 text-amber-500" data-testid="pay-mode-badge">
          {de ? "TEST / SANDBOX" : "TEST / SANDBOX"}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Tab id="methods" label={de ? "Zahlungsarten" : "Methods"} Icon={Settings2} />
        <Tab id="providers" label={de ? "Anbieter" : "Providers"} Icon={ShieldCheck} />
        <Tab id="bank" label={de ? "Banküberweisung" : "Bank Transfer"} Icon={Landmark} />
        <Tab id="logs" label={de ? "Zahlungsprotokoll" : "Payment Logs"} Icon={ReceiptText} />
      </div>

      {/* -------- METHODS -------- */}
      {tab === "methods" && (
        <div className="surface overflow-x-auto" data-testid="methods-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-muted-foreground">
              <tr>
                <th className="p-4">{de ? "Reihenfolge" : "Order"}</th>
                <th className="p-4">{de ? "Zahlungsart" : "Method"}</th>
                <th className="p-4">{de ? "Anbieter" : "Provider"}</th>
                <th className="p-4">{de ? "Währungen" : "Currencies"}</th>
                <th className="p-4">{de ? "Aktiv" : "Enabled"}</th>
                <th className="p-4 text-right">{de ? "Aktion" : "Action"}</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m.key} className="border-t border-border" data-testid={`method-row-${m.key}`}>
                  <td className="p-4 text-muted-foreground">{m.display_order}</td>
                  <td className="p-4">
                    <div className="font-semibold">{de ? m.name_de : m.name_en}</div>
                    <div className="text-xs text-muted-foreground">{m.key}</div>
                    {!m.provider_configured && m.provider !== "manual" && (
                      <span className="text-xs text-amber-500">{de ? "Zugangsdaten erforderlich" : "Credentials required"}</span>
                    )}
                  </td>
                  <td className="p-4 capitalize">{m.provider}{m.provider_mode ? ` (${m.provider_mode})` : ""}</td>
                  <td className="p-4 uppercase">{(m.currencies || []).join(", ")}</td>
                  <td className="p-4">
                    <button onClick={() => toggleMethod(m, !m.enabled)} data-testid={`method-toggle-${m.key}`}
                      className={`relative h-6 w-11 rounded-full transition-colors ${m.enabled ? "bg-primary" : "bg-secondary"}`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${m.enabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEdit({ ...m })} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`method-edit-${m.key}`}>
                      {de ? "Bearbeiten" : "Edit"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* -------- PROVIDERS -------- */}
      {tab === "providers" && (
        <div className="space-y-6">
          <StripeProvider providers={providers} webhook={webhook} reload={() => { loadProviders(); loadMethods(); }} copy={copy} de={de} />
          <PayPalProvider providers={providers} webhook={webhook} reload={() => { loadProviders(); loadMethods(); }} copy={copy} de={de} />
        </div>
      )}

      {/* -------- BANK TRANSFER -------- */}
      {tab === "bank" && bankMethod && (
        <BankTransfer method={bankMethod} reload={loadMethods} de={de}
          invoices={invoices} manual={manual} setManual={setManual} reloadInvoices={loadInvoices} />
      )}

      {/* -------- LOGS -------- */}
      {tab === "logs" && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Input testid="paylog-search" placeholder={de ? "E-Mail suchen…" : "Search email…"} value={logFilter.q} onChange={(e) => setLogFilter({ ...logFilter, q: e.target.value })} />
            <Select testid="paylog-status" value={logFilter.status} onChange={(e) => setLogFilter({ ...logFilter, status: e.target.value })}>
              <option value="">{de ? "Alle Status" : "All status"}</option>
              {["created", "pending", "processing", "paid", "failed", "cancelled", "refunded"].map((s) => <option key={s} value={s}>{sl(s)}</option>)}
            </Select>
            <Select testid="paylog-provider" value={logFilter.provider} onChange={(e) => setLogFilter({ ...logFilter, provider: e.target.value })}>
              <option value="">{de ? "Alle Anbieter" : "All providers"}</option>
              {["stripe", "paypal", "manual"].map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select testid="paylog-mode" value={logFilter.mode} onChange={(e) => setLogFilter({ ...logFilter, mode: e.target.value })}>
              <option value="">{de ? "Alle Modi" : "All modes"}</option>
              {["test", "live", "sandbox", "manual"].map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
          <div className="surface overflow-x-auto" data-testid="paylogs-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-muted-foreground"><tr>
                <th className="p-3">{de ? "Zeit" : "Time"}</th><th className="p-3">{de ? "Kunde" : "Customer"}</th>
                <th className="p-3">{de ? "Betrag" : "Amount"}</th><th className="p-3">Provider</th>
                <th className="p-3">{de ? "Methode" : "Method"}</th><th className="p-3">Mode</th>
                <th className="p-3">Status</th><th className="p-3 text-right">{de ? "Aktion" : "Action"}</th>
              </tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-border" data-testid={`paylog-${l.id}`}>
                    <td className="p-3 text-muted-foreground">{(l.created_at || "").slice(5, 16).replace("T", " ")}</td>
                    <td className="p-3">{l.user_email || "—"}</td>
                    <td className="p-3 font-semibold">{(l.amount || 0).toFixed ? l.amount.toFixed(2) : l.amount} {(l.currency || "eur").toUpperCase()}</td>
                    <td className="p-3 capitalize">{l.provider || "stripe"}</td>
                    <td className="p-3">{l.method || "—"}</td>
                    <td className="p-3 uppercase text-xs">{l.mode || "—"}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(l.payment_status)}`}>{sl(l.payment_status)}</span></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={async () => { const { data } = await api.post(`/admin/payments/logs/${l.id}/sync`); toast[data.synced ? "success" : "message"](`${de ? "Status" : "Status"}: ${data.status}`); loadLogs(); }} className="p-1.5 hover:text-primary" title="Sync" data-testid={`paylog-sync-${l.id}`}><RefreshCw size={15} /></button>
                        {l.payment_status === "paid" && (
                          <button onClick={async () => { if (!window.confirm(de ? "Zahlung erstatten?" : "Refund payment?")) return; try { await api.post(`/admin/payments/logs/${l.id}/refund`, {}); toast.success(de ? "Erstattet" : "Refunded"); loadLogs(); } catch (e) { toast.error(apiErr(e)); } }} className="p-1.5 hover:text-destructive" title="Refund" data-testid={`paylog-refund-${l.id}`}>↩</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {edit && <MethodEditor method={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadMethods(); }} de={de} />}
    </div>
  );
}

/* ---------------- Method Editor Drawer ---------------- */
function MethodEditor({ method, onClose, onSaved, de }) {
  const [m, setM] = useState({ ...method, currencies: (method.currencies || []).join(", ") });
  const set = (k, v) => setM({ ...m, [k]: v });
  const toggleType = (t) => {
    const cur = new Set(m.payment_types || []);
    cur.has(t) ? cur.delete(t) : cur.add(t);
    set("payment_types", [...cur]);
  };
  const save = async () => {
    try {
      const payload = {
        name_de: m.name_de, name_en: m.name_en, desc_de: m.desc_de, desc_en: m.desc_en,
        currencies: String(m.currencies).split(",").map((c) => c.trim()).filter(Boolean),
        default_currency: (m.default_currency || "eur").toLowerCase(),
        min_amount: Number(m.min_amount) || 0, max_amount: Number(m.max_amount) || 0,
        display_order: Number(m.display_order) || 1, payment_types: m.payment_types || [],
        active_checkout: !!m.active_checkout,
      };
      await api.put(`/admin/payments/methods/${m.key}`, payload);
      toast.success(de ? "Gespeichert" : "Saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="method-editor">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative surface w-full max-w-lg h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
          <span className="font-display font-bold">{de ? "Zahlungsart bearbeiten" : "Edit method"}: {m.key}</span>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={de ? "Name (DE)" : "Name (DE)"}><Input testid="me-name-de" value={m.name_de || ""} onChange={(e) => set("name_de", e.target.value)} /></Field>
            <Field label={de ? "Name (EN)" : "Name (EN)"}><Input testid="me-name-en" value={m.name_en || ""} onChange={(e) => set("name_en", e.target.value)} /></Field>
          </div>
          <Field label={de ? "Beschreibung (DE)" : "Description (DE)"}><Textarea testid="me-desc-de" value={m.desc_de || ""} onChange={(e) => set("desc_de", e.target.value)} /></Field>
          <Field label={de ? "Beschreibung (EN)" : "Description (EN)"}><Textarea testid="me-desc-en" value={m.desc_en || ""} onChange={(e) => set("desc_en", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={de ? "Währungen" : "Currencies"}><Input testid="me-currencies" value={m.currencies} onChange={(e) => set("currencies", e.target.value)} placeholder="eur, usd" /></Field>
            <Field label={de ? "Standard-Währung" : "Default currency"}><Input testid="me-default-currency" value={m.default_currency || "eur"} onChange={(e) => set("default_currency", e.target.value)} /></Field>
            <Field label={de ? "Reihenfolge" : "Display order"}><Input testid="me-order" type="number" value={m.display_order} onChange={(e) => set("display_order", e.target.value)} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={de ? "Min. Betrag (0 = kein Limit)" : "Min amount (0 = none)"}><Input testid="me-min" type="number" value={m.min_amount} onChange={(e) => set("min_amount", e.target.value)} /></Field>
            <Field label={de ? "Max. Betrag (0 = kein Limit)" : "Max amount (0 = none)"}><Input testid="me-max" type="number" value={m.max_amount} onChange={(e) => set("max_amount", e.target.value)} /></Field>
          </div>
          <div>
            <span className="block text-sm font-medium mb-1.5">{de ? "Zahlungsarten" : "Payment types"}</span>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_TYPES.map((t) => (
                <label key={t} className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer border ${(m.payment_types || []).includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`me-type-${t}`}>
                  <input type="checkbox" className="hidden" checked={(m.payment_types || []).includes(t)} onChange={() => toggleType(t)} />
                  {TYPE_LABEL[de ? "de" : "en"][t]}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2.5 text-sm"><input type="checkbox" checked={!!m.active_checkout} onChange={(e) => set("active_checkout", e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="me-active-checkout" /> {de ? "Im Checkout anzeigen" : "Show in checkout"}</label>
          <button onClick={save} className="btn-primary w-full" data-testid="me-save">{de ? "Speichern" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Stripe Provider ---------------- */
function StripeProvider({ providers, webhook, reload, copy, de }) {
  const p = providers.find((x) => x.id === "stripe") || {};
  const [form, setForm] = useState({ mode: "test", test_secret: "", live_secret: "", webhook_secret: "", test_publishable: "", live_publishable: "", statement_descriptor: "", confirm_password: "" });
  const [status, setStatus] = useState(null);
  useEffect(() => { setForm((f) => ({ ...f, mode: p.mode || "test", statement_descriptor: p.statement_descriptor || "", test_publishable: p.test_publishable || "", live_publishable: p.live_publishable || "" })); }, [p.mode, p.statement_descriptor, p.test_publishable, p.live_publishable]);
  const save = async () => {
    try {
      const payload = { mode: form.mode, statement_descriptor: form.statement_descriptor, test_publishable: form.test_publishable, live_publishable: form.live_publishable };
      if (form.test_secret) payload.test_secret = form.test_secret;
      if (form.live_secret) payload.live_secret = form.live_secret;
      if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;
      if (form.confirm_password) payload.confirm_password = form.confirm_password;
      await api.put("/admin/payments/providers/stripe", payload);
      toast.success(de ? "Stripe gespeichert" : "Stripe saved");
      setForm({ ...form, test_secret: "", live_secret: "", webhook_secret: "", confirm_password: "" });
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const test = async () => { try { const { data } = await api.post("/admin/payments/providers/stripe/test"); setStatus(data); toast[data.status === "ok" ? "success" : "error"](data.status); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="surface p-6 space-y-4" data-testid="provider-stripe">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-lg">Stripe</h2>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${form.mode === "live" ? "bg-green-500/15 text-green-500" : "bg-amber-500/15 text-amber-500"}`}>{form.mode === "live" ? "LIVE" : "TEST"}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={de ? "Modus" : "Mode"}><Select testid="stripe-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}><option value="test">test</option><option value="live">live</option></Select></Field>
        <Field label={de ? "Statement Descriptor" : "Statement descriptor"}><Input testid="stripe-descriptor" value={form.statement_descriptor} onChange={(e) => setForm({ ...form, statement_descriptor: e.target.value })} /></Field>
        <Field label={`Test Secret Key ${p.test_secret_set ? `(${p.test_secret_masked})` : ""}`}><Input testid="stripe-test-secret" type="password" placeholder={p.test_secret_set ? "•••• (unverändert)" : "sk_test_..."} value={form.test_secret} onChange={(e) => setForm({ ...form, test_secret: e.target.value })} /></Field>
        <Field label={`Live Secret Key ${p.live_secret_set ? `(${p.live_secret_masked})` : ""}`}><Input testid="stripe-live-secret" type="password" placeholder={p.live_secret_set ? "•••• (unverändert)" : "sk_live_..."} value={form.live_secret} onChange={(e) => setForm({ ...form, live_secret: e.target.value })} /></Field>
        <Field label={`Webhook Signing Secret ${p.webhook_secret_set ? `(${p.webhook_secret_masked})` : ""}`}><Input testid="stripe-webhook-secret" type="password" placeholder={p.webhook_secret_set ? "•••• (unverändert)" : "whsec_..."} value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} /></Field>
        <Field label="Test Publishable Key"><Input testid="stripe-test-publishable" placeholder="pk_test_..." value={form.test_publishable} onChange={(e) => setForm({ ...form, test_publishable: e.target.value })} /></Field>
        <Field label="Live Publishable Key"><Input testid="stripe-live-publishable" placeholder="pk_live_..." value={form.live_publishable} onChange={(e) => setForm({ ...form, live_publishable: e.target.value })} /></Field>
      </div>
      <div className="rounded-md bg-secondary p-3 flex items-center justify-between gap-3 text-sm">
        <span className="truncate font-mono text-xs" data-testid="stripe-webhook-url">{webhook.stripe}</span>
        <button onClick={() => copy(webhook.stripe)} className="btn-ghost !py-1.5 !px-3 text-xs shrink-0" data-testid="stripe-webhook-copy"><Copy size={13} /> {de ? "Kopieren" : "Copy"}</button>
      </div>
      <Field label={<span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> {de ? "Passwortbestätigung (für LIVE-Schlüssel / LIVE-Modus)" : "Password confirmation (for LIVE key / LIVE mode)"}</span>}>
        <Input testid="stripe-confirm" type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
      </Field>
      <div className="flex gap-2">
        <button onClick={save} className="btn-primary" data-testid="stripe-save">{de ? "Speichern" : "Save"}</button>
        <button onClick={test} className="btn-ghost" data-testid="stripe-test-btn"><RefreshCw size={15} /> {de ? "Verbindung testen" : "Test connection"}</button>
      </div>
      {status && <div className="text-sm" data-testid="stripe-test-result">Status: <b className={status.status === "ok" ? "text-green-500" : "text-red-500"}>{status.status}</b>{status.error && <span className="text-red-500"> — {status.error}</span>}</div>}
    </div>
  );
}

/* ---------------- PayPal Provider ---------------- */
function PayPalProvider({ providers, webhook, reload, copy, de }) {
  const p = providers.find((x) => x.id === "paypal") || {};
  const [form, setForm] = useState({ mode: "sandbox", client_id: "", client_secret: "", webhook_id: "", confirm_password: "" });
  const [status, setStatus] = useState(null);
  useEffect(() => { setForm((f) => ({ ...f, mode: p.mode || "sandbox", webhook_id: p.webhook_id || "" })); }, [p.mode, p.webhook_id]);
  const save = async () => {
    try {
      const payload = { mode: form.mode, webhook_id: form.webhook_id };
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      if (form.confirm_password) payload.confirm_password = form.confirm_password;
      await api.put("/admin/payments/providers/paypal", payload);
      toast.success(de ? "PayPal gespeichert" : "PayPal saved");
      setForm({ ...form, client_id: "", client_secret: "", confirm_password: "" });
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const test = async () => { try { const { data } = await api.post("/admin/payments/providers/paypal/test"); setStatus(data); toast[data.status === "ok" ? "success" : "error"](data.status); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="surface p-6 space-y-4" data-testid="provider-paypal">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-lg">PayPal</h2>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${form.mode === "live" ? "bg-green-500/15 text-green-500" : "bg-amber-500/15 text-amber-500"}`}>{form.mode === "live" ? "LIVE" : "SANDBOX"}</span>
      </div>
      <p className="text-sm text-muted-foreground">{de ? "PayPal-Zugangsdaten eingeben, um diese Zahlungsart zu aktivieren (Sandbox zum Testen)." : "Enter PayPal credentials to enable this method (Sandbox for testing)."}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={de ? "Modus" : "Mode"}><Select testid="paypal-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}><option value="sandbox">sandbox</option><option value="live">live</option></Select></Field>
        <Field label={`Webhook ID`}><Input testid="paypal-webhook-id" value={form.webhook_id} onChange={(e) => setForm({ ...form, webhook_id: e.target.value })} /></Field>
        <Field label={`Client ID ${p.client_id_set ? `(${p.client_id_masked})` : ""}`}><Input testid="paypal-client-id" type="password" placeholder={p.client_id_set ? "•••• (unverändert)" : "Client ID"} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} /></Field>
        <Field label={`Client Secret ${p.client_secret_set ? `(${p.client_secret_masked})` : ""}`}><Input testid="paypal-client-secret" type="password" placeholder={p.client_secret_set ? "•••• (unverändert)" : "Client Secret"} value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} /></Field>
      </div>
      <div className="rounded-md bg-secondary p-3 flex items-center justify-between gap-3 text-sm">
        <span className="truncate font-mono text-xs" data-testid="paypal-webhook-url">{webhook.paypal}</span>
        <button onClick={() => copy(webhook.paypal)} className="btn-ghost !py-1.5 !px-3 text-xs shrink-0" data-testid="paypal-webhook-copy"><Copy size={13} /> {de ? "Kopieren" : "Copy"}</button>
      </div>
      <Field label={<span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> {de ? "Passwortbestätigung (für LIVE-Modus)" : "Password confirmation (for LIVE mode)"}</span>}>
        <Input testid="paypal-confirm" type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
      </Field>
      <div className="flex gap-2">
        <button onClick={save} className="btn-primary" data-testid="paypal-save">{de ? "Speichern" : "Save"}</button>
        <button onClick={test} className="btn-ghost" data-testid="paypal-test-btn"><RefreshCw size={15} /> {de ? "Verbindung testen" : "Test connection"}</button>
      </div>
      {status && <div className="text-sm" data-testid="paypal-test-result">Status: <b className={status.status === "ok" ? "text-green-500" : "text-red-500"}>{status.status}</b>{status.error && <span className="text-red-500"> — {status.error}</span>}</div>}
    </div>
  );
}

/* ---------------- Bank Transfer ---------------- */
function BankTransfer({ method, reload, de, invoices, manual, setManual, reloadInvoices }) {
  const [m, setM] = useState({ ...method });
  const set = (k, v) => setM({ ...m, [k]: v });
  const save = async () => {
    try {
      await api.put("/admin/payments/methods/bank_transfer", {
        bank_holder: m.bank_holder, bank_name: m.bank_name, bank_iban: m.bank_iban, bank_bic: m.bank_bic,
        reference_format: m.reference_format, instructions_de: m.instructions_de, instructions_en: m.instructions_en,
        payment_deadline_days: Number(m.payment_deadline_days) || 14,
      });
      toast.success(de ? "Gespeichert" : "Saved"); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const record = async () => {
    if (!manual.invoice_id) { toast.error(de ? "Rechnung wählen" : "Select an invoice"); return; }
    try {
      await api.post("/admin/payments/manual-payment", { invoice_id: manual.invoice_id, amount: Number(manual.amount) || undefined, note: manual.note });
      toast.success(de ? "Zahlung erfasst" : "Payment recorded");
      setManual({ invoice_id: "", amount: "", note: "" }); reloadInvoices();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="surface p-6 space-y-4" data-testid="bank-config">
        <h2 className="font-display font-bold text-lg">{de ? "Bankverbindung" : "Bank details"}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={de ? "Kontoinhaber" : "Account holder"}><Input testid="bank-holder" value={m.bank_holder || ""} onChange={(e) => set("bank_holder", e.target.value)} /></Field>
          <Field label={de ? "Bankname" : "Bank name"}><Input testid="bank-name" value={m.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} /></Field>
          <Field label="IBAN"><Input testid="bank-iban" value={m.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} /></Field>
          <Field label="BIC"><Input testid="bank-bic" value={m.bank_bic || ""} onChange={(e) => set("bank_bic", e.target.value)} /></Field>
          <Field label={de ? "Verwendungszweck-Format" : "Reference format"}><Input testid="bank-reference" value={m.reference_format || ""} onChange={(e) => set("reference_format", e.target.value)} placeholder="RE-{invoice_number}" /></Field>
          <Field label={de ? "Zahlungsfrist (Tage)" : "Payment deadline (days)"}><Input testid="bank-deadline" type="number" value={m.payment_deadline_days || 14} onChange={(e) => set("payment_deadline_days", e.target.value)} /></Field>
        </div>
        <Field label={de ? "Zahlungshinweise (DE)" : "Instructions (DE)"}><Textarea testid="bank-instructions-de" value={m.instructions_de || ""} onChange={(e) => set("instructions_de", e.target.value)} /></Field>
        <Field label={de ? "Zahlungshinweise (EN)" : "Instructions (EN)"}><Textarea testid="bank-instructions-en" value={m.instructions_en || ""} onChange={(e) => set("instructions_en", e.target.value)} /></Field>
        <button onClick={save} className="btn-primary" data-testid="bank-save">{de ? "Speichern" : "Save"}</button>
      </div>

      <div className="surface p-6 space-y-4" data-testid="manual-payment">
        <h2 className="font-display font-bold text-lg">{de ? "Zahlungseingang erfassen" : "Record received payment"}</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={de ? "Offene Rechnung" : "Unpaid invoice"}>
            <Select testid="manual-invoice" value={manual.invoice_id} onChange={(e) => setManual({ ...manual, invoice_id: e.target.value })}>
              <option value="">{de ? "— wählen —" : "— select —"}</option>
              {invoices.map((i) => <option key={i.id} value={i.id}>{`${i.number} · ${(i.total || 0).toFixed(2)} €`}</option>)}
            </Select>
          </Field>
          <Field label={de ? "Betrag (optional)" : "Amount (optional)"}><Input testid="manual-amount" type="number" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} /></Field>
          <Field label={de ? "Notiz" : "Note"}><Input testid="manual-note" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} /></Field>
        </div>
        <button onClick={record} className="btn-primary" data-testid="manual-record"><Check size={15} /> {de ? "Als bezahlt markieren" : "Mark as paid"}</button>
      </div>
    </div>
  );
}
